import React, { useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  useTheme,
} from '@mui/material';
import { ALL_ORDERS_FILTER } from 'src/constants/key_constants';
import { OrderStatusFilterOption } from 'src/types/status_filter';

interface StatusFilterProps {
  value: string;
  onChange: (value: string) => void;
  statusFilterOptions: Map<string, OrderStatusFilterOption>;
}

export const StatusFilter: React.FC<StatusFilterProps> = ({
  value,
  onChange,
  statusFilterOptions,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const selectedOption =
    statusFilterOptions.get(value) ||
    statusFilterOptions.get(ALL_ORDERS_FILTER)!;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
    handleClose();
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <Button
        variant='outlined'
        onClick={handleClick}
        endIcon={<ArrowDropDownIcon />}
        sx={{
          height: '40px',
          minWidth: '140px',
          textTransform: 'none',
          justifyContent: 'space-between',
          borderColor: theme.palette.grey[300],
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {selectedOption.label}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement='bottom-start'
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            sx={{
              minWidth: anchorEl?.offsetWidth || 140,
              maxWidth: 200,
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <FormControl component='fieldset' sx={{ width: '100%' }}>
              <RadioGroup
                value={value}
                onChange={handleChange}
                sx={{ margin: 0 }}
              >
                {Array.from(statusFilterOptions.values()).map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={option.label}
                    sx={{
                      margin: 0,
                      padding: '8px 16px',
                      width: '100%',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                      '& .MuiFormControlLabel-label': {
                        flex: 1,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
